import { CSSProperties, FC, MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useVirtualizer } from '@tanstack/react-virtual';
import { HiOutlineEye, HiOutlineArrowDownTray } from 'react-icons/hi2';
import Preloader from '@srm/ui/preloader';
import DisplayField from '@molecules/DisplayField/DisplayField';
import ActionButton from '@molecules/RowActionButton/ActionButton';
import UserName from '@molecules/UserName';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { AttachActionTypes } from '@models/actions/AttachActionTypes';
import { ValueType } from '@srm/ui/types/ValueType';
import { fileType } from '@srm/ui/lib/fileTypeInfo';
import { findScrollParent } from '@srm/ui/lib/findScrollParent';
import { IUseAttachGridData } from '../../useAttachGridData';
import { IUseAttachSelection } from '../../useAttachSelection';
import { AttachView, IAttachColumn, IAttachRow } from '../../AttachGrid.types';
import { extOf, primaryColumn, publicationInfo, rowFileName, rowValue, userKeyOf } from '../../attachRowView';
import { VIRTUAL_THRESHOLD } from '../../attachVirtual';

interface AttachGalleryProps {
    data: IUseAttachGridData;
    selection: IUseAttachSelection;
    /** Есть хотя бы один выбранный — тогда клик по карточке выделяет её целиком. */
    selectionActive: boolean;
    view: AttachView;
    actionService?: AttachActionsService;
    onDownload?: (row: IAttachRow) => void;
    /** Файл карточки скачивается: на кнопке «Скачать» — ход, повтор недоступен. */
    isDownloading?: (row: IAttachRow) => boolean;
    onPreview?: (row: IAttachRow) => void;
    readOnly?: boolean;
}

// «Скачать/Сохранить» уже вынесено отдельной кнопкой на карточке — не дублируем его в меню действий.
const HIDDEN_CARD_ACTIONS = [AttachActionTypes[AttachActionTypes.Download]];

const isVersionCol = (c: IAttachColumn): boolean => /Version/i.test(c.field);
const isPersonCol = (c: IAttachColumn): boolean => !!c.userKeyField || c.dataType === ValueType.AddressBook;
const isPublicationCol = (c: IAttachColumn): boolean => c.isStatusColumn || /Publication/i.test(c.field);
const versionCurrent = (v: unknown): string => {
    const s = v == null ? '' : String(v);
    return s.split('/')[0] || '';
};

/** Параметры сетки по виду: мин. ширина карточки, gap, оценка высоты ряда. */
const GRID = {
    cards: { min: 232, gap: 16, est: 210 },
    compact: { min: 160, gap: 10, est: 150 },
} as const;

/** Виды «Карточки» и «Мелкие значки». Файл-как-карточка с маркой типа,
 *  ext-пилюлей и метаданными; имя — ссылка на файл; автор — с hover-карточкой.
 *  В режиме «Все» (много карточек) — виртуализация рядов по внешнему скроллу страницы. */
const AttachGallery: FC<AttachGalleryProps> = ({
    data,
    selection,
    selectionActive,
    view,
    actionService,
    onDownload,
    isDownloading,
    onPreview,
    readOnly = false,
}) => {
    const pointerStart = useRef<{ x: number; y: number } | null>(null);
    const visible = data.columns.filter((c) => c.show);
    const primary = primaryColumn(data.columns);
    const metaCols = visible.filter((c) => c.field !== primary?.field).slice(0, 3);

    const renderMeta = (c: IAttachColumn, row: IAttachRow) => {
        if (isPublicationCol(c)) {
            const { text, published } = publicationInfo(rowValue(row, c.field));
            return (
                <span className={clsx('atb__pub', published ? 'atb__pub--yes' : 'atb__pub--no')}>{text || '—'}</span>
            );
        }
        if (isPersonCol(c)) {
            const name = rowValue(row, c.field);
            const nameStr = name == null ? '' : String(name);
            if (!nameStr) return <span className="atb__cell-muted">—</span>;
            if (readOnly) return <span>{nameStr}</span>;
            return <UserName userKey={userKeyOf(c, row)} name={nameStr} withIcon avatar />;
        }
        if (isVersionCol(c)) {
            return <span>версия {versionCurrent(rowValue(row, c.field))}</span>;
        }
        return <DisplayField type={c.dataType} value={rowValue(row, c.field)} />;
    };

    // Клик по карточке в режиме выбора выделяет её целиком (кроме действий/чекбокса).
    const onCardClick = (row: IAttachRow) => (e: MouseEvent) => {
        if (!selectionActive) return;
        const t = e.target as HTMLElement;
        if (t.closest('.atb__card-actions') || t.closest('.atb__card-check')) return;
        e.preventDefault();
        selection.toggle(row.key);
    };

    const renderCard = (row: IAttachRow) => {
        const name = rowFileName(row, primary?.field);
        const ext = extOf(name);
        const t = fileType(ext);
        const Icon = t.Icon;
        const sel = selection.isSelected(row.key);
        const downloading = isDownloading?.(row) ?? false;
        return (
            // выбор картой — мышиная доп.возможность; доступный контрол выбора — чекбокс ниже
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
            <article
                key={row.key}
                data-row-key={row.key}
                className={clsx('atb__card', sel && 'is-selected', selectionActive && 'is-selectable')}
                style={
                    {
                        '--tile-accent': t.accent,
                        '--tile-accent-soft': t.accentSoft,
                        '--tile-accent-ink': t.accentInk,
                    } as CSSProperties
                }
                onPointerDownCapture={(e) => {
                    pointerStart.current = { x: e.clientX, y: e.clientY };
                }}
                onClickCapture={(e) => {
                    const start = pointerStart.current;
                    pointerStart.current = null;
                    if (e.detail === 0 || !start || Math.hypot(e.clientX - start.x, e.clientY - start.y) < 3) return;
                    const target = e.target as HTMLElement;
                    if (target.closest('.atb__card-actions, .atb__card-check')) return;
                    const textSelection = e.currentTarget.ownerDocument.getSelection();
                    if (textSelection && !textSelection.isCollapsed && textSelection.containsNode(e.currentTarget, true)) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
                onClick={onCardClick(row)}
            >
                {!readOnly && (
                    <span className="atb__card-check">
                        <input
                            type="checkbox"
                            className="atb-check"
                            checked={sel}
                            onChange={() => selection.toggle(row.key)}
                            aria-label={`Выбрать ${name}`}
                        />
                    </span>
                )}

                {!readOnly && onPreview && onDownload && actionService && (
                    <div className="atb__card-actions">
                        <button
                            type="button"
                            className="atb__card-act"
                            title="Предпросмотр"
                            onClick={() => onPreview(row)}
                        >
                            <HiOutlineEye aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="atb__card-act atb__card-act--primary"
                            title="Скачать"
                            aria-busy={downloading || undefined}
                            disabled={downloading}
                            onClick={() => onDownload(row)}
                        >
                            {downloading ? (
                                <Preloader size={16} variant="inherit" position="inline" />
                            ) : (
                                <HiOutlineArrowDownTray aria-hidden />
                            )}
                        </button>
                        <span className="atb__card-act atb__card-act--menu">
                            <ActionButton
                                objId={row.key}
                                objData={row.raw}
                                service={actionService}
                                onModifyData={data.refresh}
                                excludeActionTypes={HIDDEN_CARD_ACTIONS}
                            />
                        </span>
                    </div>
                )}

                <div className="atb__card-top">
                    <span className="atb__card-icon">
                        <Icon aria-hidden />
                    </span>
                    {ext && <span className="atb__card-ext">{ext}</span>}
                </div>

                <span className="atb__card-type">{t.label}</span>
                {readOnly ? (
                    <span className="atb__card-title" title={name}>
                        {name}
                    </span>
                ) : (
                    <button
                        type="button"
                        className="atb__card-title"
                        title={name}
                        onClick={() => {
                            if (!selectionActive) onDownload?.(row);
                        }}
                    >
                        {name}
                    </button>
                )}

                {metaCols.length > 0 && (
                    <div className="atb__card-foot">
                        {metaCols.map((c, i) => (
                            <span key={c.field} className="atb__card-foot-item">
                                {i > 0 && <span className="atb__dot" />}
                                {renderMeta(c, row)}
                            </span>
                        ))}
                    </div>
                )}
            </article>
        );
    };

    // ----- Виртуализация рядов карточек (режим «Все»/«20») по внешнему скроллу -----
    const compact = view === 'compact';
    const g = compact ? GRID.compact : GRID.cards;
    const rows = data.rows;
    const virtual = rows.length > VIRTUAL_THRESHOLD;

    const wrapRef = useRef<HTMLDivElement>(null);
    const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
    const [scrollMargin, setScrollMargin] = useState(0);
    const [cols, setCols] = useState(0);

    // Число колонок — из ширины контейнера (повторяет auto-fill minmax(min, 1fr)).
    useLayoutEffect(() => {
        if (!virtual) return;
        const el = wrapRef.current;
        if (!el) return;
        const update = () =>
            setCols((prev) => {
                const next = Math.max(1, Math.floor((el.clientWidth + g.gap) / (g.min + g.gap)));
                return prev === next ? prev : next;
            });
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [virtual, g.gap, g.min]);

    const colCount = cols || 1;
    const rowCount = Math.ceil(rows.length / colCount);

    const virtualizer = useVirtualizer({
        enabled: virtual && cols > 0,
        count: rowCount,
        getScrollElement: () => scrollParent,
        estimateSize: () => g.est,
        getItemKey: (i) => `r${i}`,
        overscan: 4,
        gap: g.gap,
        scrollMargin,
    });

    // Внешний скролл-контейнер + смещение сетки внутри него (как в таблице).
    useLayoutEffect(() => {
        if (!virtual) {
            setScrollParent(null);
            return;
        }
        const parent = findScrollParent(wrapRef.current);
        setScrollParent(parent);
        const measure = () => {
            const el = wrapRef.current;
            if (!el || !parent) return;
            const top = el.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop;
            setScrollMargin((prev) => (Math.abs(prev - top) < 1 ? prev : top));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [virtual, view, cols, rows.length, data.columns]);

    if (virtual) {
        return (
            <div
                ref={wrapRef}
                className={clsx('atb__cards-vwrap', compact && 'is-compact')}
                style={{ position: 'relative', height: cols > 0 ? virtualizer.getTotalSize() : undefined }}
            >
                {cols > 0 &&
                    virtualizer.getVirtualItems().map((vr) => {
                        const start = vr.index * colCount;
                        return (
                            <div
                                key={vr.key}
                                data-index={vr.index}
                                ref={virtualizer.measureElement}
                                className={clsx('atb__cards-row', compact && 'is-compact')}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${vr.start - scrollMargin}px)`,
                                    gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
                                }}
                            >
                                {rows.slice(start, start + colCount).map(renderCard)}
                            </div>
                        );
                    })}
            </div>
        );
    }

    return (
        <div ref={wrapRef} className={clsx('atb__cards', compact && 'is-compact')}>
            {rows.map(renderCard)}
        </div>
    );
};

export default AttachGallery;
